/**
 * Spark's idle head motor.
 *
 * Humans do not continuously sweep their head through smooth noise. They hold
 * a fixation, make a short re-orientation, then hold again — but a held look
 * is not a frozen pose. This controller models that cadence plus sub-degree
 * postural corrections and an irregular breathing bias, while leaving
 * semantic target choice to SparkBehavior / AIOrchestratorModel.
 *
 * The controller is deterministic for a seed and samples randomness only at
 * fixation, correction, and breath boundaries. That keeps its timing stable
 * across frame rates and makes the motion drivable without a renderer or DOM.
 */

export type IdleAttentionKind = "user" | "model";

export interface IdleVisualCue {
  /** Cue direction relative to the semantic gaze target. */
  readonly yawOffsetDeg: number;
  readonly pitchOffsetDeg: number;
}

export interface IdleHeadOffsets {
  readonly yawDeg: number;
  readonly pitchDeg: number;
  readonly rollDeg: number;
}

type IdlePhase = "suppressed" | "holding" | "shifting";
type MicroPhase = "holding" | "shifting";
type UserCheckInPhase = "waiting" | "turning" | "holding" | "returning";
type HeadIntrusionPhase = "inactive" | "user" | "pointer";

const K_MAX_FRAME_STEP_S = 0.1;
const K_PHASE_EPSILON_S = 1e-6;
const K_OFFSET_SNAP_EPSILON_DEG = 0.005;

// A head turn uses a minimum-jerk velocity profile. Small corrections finish
// quickly; the occasional shared-attention glance is still below half a
// second, then earns a visible fixation instead of immediately drifting on.
const K_SHIFT_MIN_S = 0.18;
const K_SHIFT_MAX_S = 0.48;
const K_SHIFT_BASE_S = 0.14;
const K_SHIFT_S_PER_DEG = 0.026;
const K_SHIFT_DURATION_JITTER = 0.1;

// User direction: the approved large looks should occur twice as often. The
// ranges are the original cadence divided by two; micro posture and social
// check-ins keep their independent timings.
const K_USER_HOLD_MIN_S = 1.2;
const K_USER_HOLD_MAX_S = 2.7;
const K_MODEL_HOLD_MIN_S = 0.85;
const K_MODEL_HOLD_MAX_S = 1.9;
const K_INITIAL_HOLD_MIN_S = 0.5;
const K_INITIAL_HOLD_MAX_S = 1.1;
const K_GLANCE_HOLD_MIN_S = 0.55;
const K_GLANCE_HOLD_MAX_S = 1.05;

// Looking at a person is socially stable: most changes are tiny corrections,
// with occasional gaze aversion or a glance toward what the user is viewing.
const K_SHARED_ATTENTION_CHANCE = 0.24;
const K_GAZE_AVERSION_CHANCE = 0.16;
const K_MIN_SHARED_CUE_DEG = 1.25;
const K_USER_CORRECTION_YAW_DEG = 1.6;
const K_USER_CORRECTION_PITCH_DEG = 0.8;
const K_USER_AVERSION_YAW_MIN_DEG = 4.0;
const K_USER_AVERSION_YAW_MAX_DEG = 8.5;
const K_USER_AVERSION_PITCH_MIN_DEG = -4.0;
const K_USER_AVERSION_PITCH_MAX_DEG = -1.0;
const K_SHARED_YAW_LIMIT_DEG = 12.0;
const K_SHARED_PITCH_DOWN_LIMIT_DEG = -7.0;
const K_SHARED_PITCH_UP_LIMIT_DEG = 6.0;

// A model is inspected across its apparent extent, but the head only carries
// the broad attention shift (eyes would carry the rest on a real person).
const K_MODEL_CENTER_CHANCE = 0.2;
const K_MODEL_SPAN_SCALE = 0.55;
const K_MODEL_SPAN_MIN_DEG = 1.2;
const K_MODEL_SPAN_MAX_DEG = 7.5;
const K_MODEL_POINT_SPAN_DEG = 0.35;
const K_MODEL_PITCH_TO_YAW_RATIO = 0.65;

const K_RETURN_JITTER_YAW_DEG = 0.35;
const K_RETURN_JITTER_PITCH_DEG = 0.2;
const K_ROLL_FROM_YAW = -0.06;
const K_ROLL_JITTER_DEG = 0.3;
const K_ROLL_LIMIT_DEG = 1.4;

// Purposeful activity suppresses idle mannerisms and brings the offset home
// promptly without snapping the rendered rotation.
const K_SUPPRESSED_RETURN_SHARPNESS = 12.0;

// Eye contact stays alive through small absolute (not cumulative) posture
// corrections. A separate random stream is load-bearing: tuning this layer
// must not reshuffle the already-approved larger glance sequence.
const K_MICRO_RANDOM_SALT = 0x9e3779b9;
const K_BREATH_RANDOM_SALT = 0xc2b2ae35;
const K_MICRO_INITIAL_HOLD_MIN_S = 0.18;
const K_MICRO_INITIAL_HOLD_MAX_S = 0.48;
const K_MICRO_HOLD_MIN_S = 0.35;
const K_MICRO_HOLD_MAX_S = 0.85;
const K_MICRO_SHIFT_MIN_S = 0.18;
const K_MICRO_SHIFT_MAX_S = 0.32;
const K_MICRO_YAW_LIMIT_DEG = 0.65;
const K_MICRO_PITCH_LIMIT_DEG = 0.34;
const K_MICRO_ROLL_LIMIT_DEG = 0.16;
const K_MICRO_ROLL_FROM_YAW = -0.08;
const K_MICRO_ROLL_JITTER_DEG = 0.08;
const K_MICRO_MIN_CHANGE_DEG = 0.1;
const K_MICRO_FADE_IN_SHARPNESS = 5.0;
const K_MICRO_FADE_OUT_SHARPNESS = 12.0;
const K_MICRO_MACRO_FADE_START_DEG = 2.5;
const K_MICRO_MACRO_FADE_END_DEG = 5.0;

// Breathing is mostly pitch, like a real head riding the upper body. The
// cycle is asymmetric and re-sampled at every zero crossing so it never reads
// as a perfectly looping sine and amplitude changes cannot introduce a pop.
const K_BREATH_DURATION_MIN_S = 3.0;
const K_BREATH_DURATION_MAX_S = 4.8;
const K_BREATH_PITCH_MIN_DEG = 0.1;
const K_BREATH_PITCH_MAX_DEG = 0.18;
const K_BREATH_EXHALE_MIN_FRACTION = 0.56;
const K_BREATH_EXHALE_MAX_FRACTION = 0.64;

// A non-user subject earns an occasional social check-in. The physical turn
// lives in SparkBehavior because it can be far larger than an idle offset;
// this pure controller owns only its irregular timing and phase.
const K_CHECK_IN_RANDOM_SALT = 0x85ebca6b;
// Shared-attention looks should feel conversational rather than rare. These
// are half the original waits (5.5–9 s initially, 9–15 s thereafter); the
// turn and hold durations stay unchanged so each glance remains readable.
const K_CHECK_IN_INITIAL_WAIT_MIN_S = 2.75;
const K_CHECK_IN_INITIAL_WAIT_MAX_S = 4.5;
const K_CHECK_IN_REPEAT_WAIT_MIN_S = 4.5;
const K_CHECK_IN_REPEAT_WAIT_MAX_S = 7.5;
const K_CHECK_IN_HOLD_MIN_S = 0.65;
const K_CHECK_IN_HOLD_MAX_S = 0.95;

// A pointer entering Spark's personal space is intentionally not an ordinary
// social check-in. It is an alert "what are you doing?" exchange that keeps
// making irregular user↔pointer checks while the cursor remains in the zone.
const K_HEAD_INTRUSION_RANDOM_SALT = 0x27d4eb2f;
const K_HEAD_INTRUSION_USER_HOLD_S = 0.5;
const K_HEAD_INTRUSION_POINTER_HOLD_MIN_S = 0.32;
const K_HEAD_INTRUSION_POINTER_HOLD_MAX_S = 0.48;
const K_HEAD_INTRUSION_REPEAT_WAIT_MIN_S = 2.0;
const K_HEAD_INTRUSION_REPEAT_WAIT_MAX_S = 4.0;

const K_FALLBACK_RANDOM_STATE = 0x6d2b79f5;

interface Fixation {
  readonly yawDeg: number;
  readonly pitchDeg: number;
  readonly rollDeg: number;
  readonly holdSeconds: number;
}

/**
 * Seeded, frame-clock-driven fixation controller for Spark's relative head
 * pose. The returned object is reused; callers must read it, not retain and
 * mutate it.
 */
export class NaturalIdleHeadMotion {
  private m_randomState: number;
  private m_microRandomState: number;
  private m_breathRandomState: number;
  private m_phase: IdlePhase = "suppressed";
  private m_attentionKind: IdleAttentionKind | null = null;
  private m_phaseRemainingS = 0;
  private m_shiftElapsedS = 0;
  private m_shiftDurationS = K_SHIFT_MIN_S;
  private m_holdAfterShiftS = K_INITIAL_HOLD_MIN_S;
  private m_returnToAnchor = false;

  private m_yawDeg = 0;
  private m_pitchDeg = 0;
  private m_rollDeg = 0;
  private m_shiftStartYawDeg = 0;
  private m_shiftStartPitchDeg = 0;
  private m_shiftStartRollDeg = 0;
  private m_shiftTargetYawDeg = 0;
  private m_shiftTargetPitchDeg = 0;
  private m_shiftTargetRollDeg = 0;

  private m_microActive = false;
  private m_microPhase: MicroPhase = "holding";
  private m_microPhaseRemainingS = 0;
  private m_microShiftElapsedS = 0;
  private m_microShiftDurationS = K_MICRO_SHIFT_MIN_S;
  private m_microVisibility = 0;
  private m_microYawDeg = 0;
  private m_microPitchDeg = 0;
  private m_microRollDeg = 0;
  private m_microStartYawDeg = 0;
  private m_microStartPitchDeg = 0;
  private m_microStartRollDeg = 0;
  private m_microTargetYawDeg = 0;
  private m_microTargetPitchDeg = 0;
  private m_microTargetRollDeg = 0;
  private m_breathElapsedS = 0;
  private m_breathDurationS = K_BREATH_DURATION_MIN_S;
  private m_breathPitchAmplitudeDeg = K_BREATH_PITCH_MIN_DEG;
  private m_breathExhaleFraction = K_BREATH_EXHALE_MIN_FRACTION;

  private readonly m_offsets = { yawDeg: 0, pitchDeg: 0, rollDeg: 0 };

  constructor(
    seed: number,
    private readonly m_visualCue?: () => IdleVisualCue | null,
  ) {
    const normalizedSeed = Number.isFinite(seed) ? Math.floor(seed) >>> 0 : 0;
    this.m_randomState = normalizedSeed || K_FALLBACK_RANDOM_STATE;
    this.m_microRandomState =
      (this.m_randomState ^ K_MICRO_RANDOM_SALT) >>> 0 || K_FALLBACK_RANDOM_STATE;
    this.m_breathRandomState =
      (this.m_randomState ^ K_BREATH_RANDOM_SALT) >>> 0 || K_FALLBACK_RANDOM_STATE;
  }

  /** Clear a stale mannerism when Spark is shown or its lifecycle restarts. */
  reset(): void {
    this.m_phase = "suppressed";
    this.m_attentionKind = null;
    this.m_phaseRemainingS = 0;
    this.m_shiftElapsedS = 0;
    this.m_returnToAnchor = false;
    this.m_yawDeg = 0;
    this.m_pitchDeg = 0;
    this.m_rollDeg = 0;
    this.resetMicroMotion();
    this.publishOffsets();
  }

  update(
    deltaSeconds: number,
    enabled: boolean,
    attentionKind: IdleAttentionKind,
    targetAngularRadiusDeg: number,
  ): IdleHeadOffsets {
    const dt = clamp(
      Number.isFinite(deltaSeconds) ? deltaSeconds : 0,
      0,
      K_MAX_FRAME_STEP_S,
    );

    if (!enabled) {
      this.suppress(attentionKind, dt);
      this.updateMicroMotion(dt, false);
      return this.publishOffsets();
    }

    if (this.m_phase === "suppressed" || this.m_attentionKind !== attentionKind) {
      this.m_attentionKind = attentionKind;
      this.m_phase = "holding";
      this.m_phaseRemainingS = this.randomRange(K_INITIAL_HOLD_MIN_S, K_INITIAL_HOLD_MAX_S);
      this.m_returnToAnchor = false;
    }

    let remainingS = dt;
    while (remainingS > K_PHASE_EPSILON_S) {
      if (this.m_phase === "holding") {
        const consumedS = Math.min(remainingS, this.m_phaseRemainingS);
        this.m_phaseRemainingS -= consumedS;
        remainingS -= consumedS;
        if (this.m_phaseRemainingS <= K_PHASE_EPSILON_S) {
          this.beginNextShift(attentionKind, targetAngularRadiusDeg);
        }
        continue;
      }

      if (this.m_phase === "shifting") {
        const shiftRemainingS = this.m_shiftDurationS - this.m_shiftElapsedS;
        const consumedS = Math.min(remainingS, shiftRemainingS);
        this.m_shiftElapsedS += consumedS;
        remainingS -= consumedS;
        this.evaluateShift();
        if (this.m_shiftElapsedS + K_PHASE_EPSILON_S >= this.m_shiftDurationS) {
          this.finishShift();
        }
        continue;
      }

      // `enabled` has already moved us out of suppressed. Keep this guard so
      // a future phase addition cannot turn a malformed state into a busy loop.
      break;
    }

    this.updateMicroMotion(dt, attentionKind === "user");

    return this.publishOffsets();
  }

  private suppress(attentionKind: IdleAttentionKind, dt: number): void {
    this.m_phase = "suppressed";
    this.m_attentionKind = attentionKind;
    this.m_phaseRemainingS = 0;
    this.m_shiftElapsedS = 0;
    this.m_returnToAnchor = false;

    const t = 1 - Math.exp(-K_SUPPRESSED_RETURN_SHARPNESS * dt);
    this.m_yawDeg = approachZero(this.m_yawDeg, t);
    this.m_pitchDeg = approachZero(this.m_pitchDeg, t);
    this.m_rollDeg = approachZero(this.m_rollDeg, t);
  }

  private beginNextShift(
    attentionKind: IdleAttentionKind,
    targetAngularRadiusDeg: number,
  ): void {
    const fixation = attentionKind === "user"
      ? this.sampleUserFixation()
      : this.sampleModelFixation(targetAngularRadiusDeg);

    this.m_shiftStartYawDeg = this.m_yawDeg;
    this.m_shiftStartPitchDeg = this.m_pitchDeg;
    this.m_shiftStartRollDeg = this.m_rollDeg;
    this.m_shiftTargetYawDeg = fixation.yawDeg;
    this.m_shiftTargetPitchDeg = fixation.pitchDeg;
    this.m_shiftTargetRollDeg = fixation.rollDeg;
    this.m_holdAfterShiftS = fixation.holdSeconds;
    this.m_shiftElapsedS = 0;

    const distanceDeg = Math.max(
      Math.abs(this.m_shiftTargetYawDeg - this.m_yawDeg),
      Math.abs(this.m_shiftTargetPitchDeg - this.m_pitchDeg),
      Math.abs(this.m_shiftTargetRollDeg - this.m_rollDeg),
    );
    const durationJitter = this.randomRange(
      1 - K_SHIFT_DURATION_JITTER,
      1 + K_SHIFT_DURATION_JITTER,
    );
    this.m_shiftDurationS = clamp(
      (K_SHIFT_BASE_S + distanceDeg * K_SHIFT_S_PER_DEG) * durationJitter,
      K_SHIFT_MIN_S,
      K_SHIFT_MAX_S,
    );
    this.m_phase = "shifting";
  }

  private evaluateShift(): void {
    const t = clamp(this.m_shiftElapsedS / this.m_shiftDurationS, 0, 1);
    const eased = minimumJerk(t);
    this.m_yawDeg = lerp(this.m_shiftStartYawDeg, this.m_shiftTargetYawDeg, eased);
    this.m_pitchDeg = lerp(this.m_shiftStartPitchDeg, this.m_shiftTargetPitchDeg, eased);
    this.m_rollDeg = lerp(this.m_shiftStartRollDeg, this.m_shiftTargetRollDeg, eased);
  }

  private finishShift(): void {
    this.m_yawDeg = this.m_shiftTargetYawDeg;
    this.m_pitchDeg = this.m_shiftTargetPitchDeg;
    this.m_rollDeg = this.m_shiftTargetRollDeg;
    this.m_phase = "holding";
    this.m_phaseRemainingS = this.m_holdAfterShiftS;
  }

  private sampleUserFixation(): Fixation {
    if (this.m_returnToAnchor) {
      this.m_returnToAnchor = false;
      const yawDeg = this.centeredRandom() * K_RETURN_JITTER_YAW_DEG;
      const pitchDeg = this.centeredRandom() * K_RETURN_JITTER_PITCH_DEG;
      return this.fixation(
        yawDeg,
        pitchDeg,
        this.rollForYaw(yawDeg),
        this.userHoldSeconds(),
      );
    }

    const event = this.nextRandom();
    if (event < K_SHARED_ATTENTION_CHANCE) {
      const cue = this.m_visualCue?.() ?? null;
      if (cue && Number.isFinite(cue.yawOffsetDeg) && Number.isFinite(cue.pitchOffsetDeg)) {
        const yawDeg = clamp(cue.yawOffsetDeg, -K_SHARED_YAW_LIMIT_DEG, K_SHARED_YAW_LIMIT_DEG);
        const pitchDeg = clamp(
          cue.pitchOffsetDeg,
          K_SHARED_PITCH_DOWN_LIMIT_DEG,
          K_SHARED_PITCH_UP_LIMIT_DEG,
        );
        if (Math.hypot(yawDeg, pitchDeg) >= K_MIN_SHARED_CUE_DEG) {
          this.m_returnToAnchor = true;
          return this.fixation(
            yawDeg,
            pitchDeg,
            this.rollForYaw(yawDeg),
            this.randomRange(K_GLANCE_HOLD_MIN_S, K_GLANCE_HOLD_MAX_S),
          );
        }
      }
    } else if (event < K_SHARED_ATTENTION_CHANCE + K_GAZE_AVERSION_CHANCE) {
      const direction = this.nextRandom() < 0.5 ? -1 : 1;
      const yawDeg = direction * this.randomRange(
        K_USER_AVERSION_YAW_MIN_DEG,
        K_USER_AVERSION_YAW_MAX_DEG,
      );
      const pitchDeg = this.randomRange(
        K_USER_AVERSION_PITCH_MIN_DEG,
        K_USER_AVERSION_PITCH_MAX_DEG,
      );
      this.m_returnToAnchor = true;
      return this.fixation(
        yawDeg,
        pitchDeg,
        this.rollForYaw(yawDeg),
        this.randomRange(K_GLANCE_HOLD_MIN_S, K_GLANCE_HOLD_MAX_S),
      );
    }

    const yawDeg = this.centeredRandom() * K_USER_CORRECTION_YAW_DEG;
    const pitchDeg = this.centeredRandom() * K_USER_CORRECTION_PITCH_DEG;
    return this.fixation(
      yawDeg,
      pitchDeg,
      this.rollForYaw(yawDeg),
      this.userHoldSeconds(),
    );
  }

  private sampleModelFixation(targetAngularRadiusDeg: number): Fixation {
    const spanDeg = targetAngularRadiusDeg > 0
      ? clamp(
        Math.abs(targetAngularRadiusDeg) * K_MODEL_SPAN_SCALE,
        K_MODEL_SPAN_MIN_DEG,
        K_MODEL_SPAN_MAX_DEG,
      )
      : K_MODEL_POINT_SPAN_DEG;
    const center = this.nextRandom() < K_MODEL_CENTER_CHANCE;
    const yawDeg = center
      ? this.centeredRandom() * K_RETURN_JITTER_YAW_DEG
      : this.centeredRandom() * spanDeg;
    const pitchDeg = center
      ? this.centeredRandom() * K_RETURN_JITTER_PITCH_DEG
      : this.centeredRandom() * spanDeg * K_MODEL_PITCH_TO_YAW_RATIO;
    return this.fixation(
      yawDeg,
      pitchDeg,
      this.rollForYaw(yawDeg),
      this.randomRange(K_MODEL_HOLD_MIN_S, K_MODEL_HOLD_MAX_S),
    );
  }

  private fixation(
    yawDeg: number,
    pitchDeg: number,
    rollDeg: number,
    holdSeconds: number,
  ): Fixation {
    return {
      yawDeg: clamp(yawDeg, -K_SHARED_YAW_LIMIT_DEG, K_SHARED_YAW_LIMIT_DEG),
      pitchDeg: clamp(
        pitchDeg,
        K_SHARED_PITCH_DOWN_LIMIT_DEG,
        K_SHARED_PITCH_UP_LIMIT_DEG,
      ),
      rollDeg: clamp(rollDeg, -K_ROLL_LIMIT_DEG, K_ROLL_LIMIT_DEG),
      holdSeconds,
    };
  }

  private rollForYaw(yawDeg: number): number {
    return yawDeg * K_ROLL_FROM_YAW + this.centeredRandom() * K_ROLL_JITTER_DEG;
  }

  private userHoldSeconds(): number {
    return this.randomRange(K_USER_HOLD_MIN_S, K_USER_HOLD_MAX_S);
  }

  private resetMicroMotion(): void {
    this.m_microActive = false;
    this.m_microPhase = "holding";
    this.m_microPhaseRemainingS = 0;
    this.m_microShiftElapsedS = 0;
    this.m_microVisibility = 0;
    this.m_microYawDeg = 0;
    this.m_microPitchDeg = 0;
    this.m_microRollDeg = 0;
    this.m_microTargetYawDeg = 0;
    this.m_microTargetPitchDeg = 0;
    this.m_microTargetRollDeg = 0;
    this.m_breathElapsedS = 0;
  }

  private updateMicroMotion(dt: number, active: boolean): void {
    const visibilityT = 1 - Math.exp(
      -(active ? K_MICRO_FADE_IN_SHARPNESS : K_MICRO_FADE_OUT_SHARPNESS) * dt,
    );
    this.m_microVisibility = lerp(this.m_microVisibility, active ? 1 : 0, visibilityT);

    if (!active) {
      this.m_microActive = false;
      return;
    }

    if (!this.m_microActive) {
      this.m_microActive = true;
      this.m_microPhase = "holding";
      this.m_microPhaseRemainingS = this.microRandomRange(
        K_MICRO_INITIAL_HOLD_MIN_S,
        K_MICRO_INITIAL_HOLD_MAX_S,
      );
      this.m_microShiftElapsedS = 0;
      this.m_microYawDeg = 0;
      this.m_microPitchDeg = 0;
      this.m_microRollDeg = 0;
      this.beginBreath();
    }

    let remainingS = dt;
    while (remainingS > K_PHASE_EPSILON_S) {
      if (this.m_microPhase === "holding") {
        const consumedS = Math.min(remainingS, this.m_microPhaseRemainingS);
        this.m_microPhaseRemainingS -= consumedS;
        remainingS -= consumedS;
        if (this.m_microPhaseRemainingS <= K_PHASE_EPSILON_S) {
          this.beginMicroShift();
        }
        continue;
      }

      const shiftRemainingS = this.m_microShiftDurationS - this.m_microShiftElapsedS;
      const consumedS = Math.min(remainingS, shiftRemainingS);
      this.m_microShiftElapsedS += consumedS;
      remainingS -= consumedS;
      this.evaluateMicroShift();
      if (this.m_microShiftElapsedS + K_PHASE_EPSILON_S >= this.m_microShiftDurationS) {
        this.finishMicroShift();
      }
    }

    this.advanceBreath(dt);
  }

  private beginMicroShift(): void {
    this.m_microStartYawDeg = this.m_microYawDeg;
    this.m_microStartPitchDeg = this.m_microPitchDeg;
    this.m_microStartRollDeg = this.m_microRollDeg;

    // Retry a near-identical draw twice. The third is accepted even when tiny
    // so the random stream stays bounded and a quiet correction remains legal.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const yawDeg = this.microCenteredRandom() * K_MICRO_YAW_LIMIT_DEG;
      const pitchDeg = this.microCenteredRandom() * K_MICRO_PITCH_LIMIT_DEG;
      const rollDeg = clamp(
        yawDeg * K_MICRO_ROLL_FROM_YAW +
          this.microCenteredRandom() * K_MICRO_ROLL_JITTER_DEG,
        -K_MICRO_ROLL_LIMIT_DEG,
        K_MICRO_ROLL_LIMIT_DEG,
      );
      this.m_microTargetYawDeg = yawDeg;
      this.m_microTargetPitchDeg = pitchDeg;
      this.m_microTargetRollDeg = rollDeg;
      if (
        attempt === 2 ||
        Math.hypot(
          yawDeg - this.m_microYawDeg,
          pitchDeg - this.m_microPitchDeg,
        ) >= K_MICRO_MIN_CHANGE_DEG
      ) {
        break;
      }
    }

    this.m_microShiftDurationS = this.microRandomRange(
      K_MICRO_SHIFT_MIN_S,
      K_MICRO_SHIFT_MAX_S,
    );
    this.m_microShiftElapsedS = 0;
    this.m_microPhase = "shifting";
  }

  private evaluateMicroShift(): void {
    const eased = minimumJerk(
      clamp(this.m_microShiftElapsedS / this.m_microShiftDurationS, 0, 1),
    );
    this.m_microYawDeg = lerp(this.m_microStartYawDeg, this.m_microTargetYawDeg, eased);
    this.m_microPitchDeg = lerp(
      this.m_microStartPitchDeg,
      this.m_microTargetPitchDeg,
      eased,
    );
    this.m_microRollDeg = lerp(this.m_microStartRollDeg, this.m_microTargetRollDeg, eased);
  }

  private finishMicroShift(): void {
    this.m_microYawDeg = this.m_microTargetYawDeg;
    this.m_microPitchDeg = this.m_microTargetPitchDeg;
    this.m_microRollDeg = this.m_microTargetRollDeg;
    this.m_microPhase = "holding";
    this.m_microPhaseRemainingS = this.microRandomRange(
      K_MICRO_HOLD_MIN_S,
      K_MICRO_HOLD_MAX_S,
    );
  }

  private beginBreath(): void {
    this.m_breathElapsedS = 0;
    this.m_breathDurationS = this.breathRandomRange(
      K_BREATH_DURATION_MIN_S,
      K_BREATH_DURATION_MAX_S,
    );
    this.m_breathPitchAmplitudeDeg = this.breathRandomRange(
      K_BREATH_PITCH_MIN_DEG,
      K_BREATH_PITCH_MAX_DEG,
    );
    this.m_breathExhaleFraction = this.breathRandomRange(
      K_BREATH_EXHALE_MIN_FRACTION,
      K_BREATH_EXHALE_MAX_FRACTION,
    );
  }

  private advanceBreath(dt: number): void {
    this.m_breathElapsedS += dt;
    while (this.m_breathElapsedS >= this.m_breathDurationS) {
      this.m_breathElapsedS -= this.m_breathDurationS;
      const overflowS = this.m_breathElapsedS;
      this.beginBreath();
      this.m_breathElapsedS = overflowS;
    }
  }

  private breathPitchDeg(): number {
    const cycleT = clamp(this.m_breathElapsedS / this.m_breathDurationS, 0, 1);
    const inhaleFraction = 1 - this.m_breathExhaleFraction;
    const phase = cycleT < inhaleFraction
      ? Math.PI * (cycleT / inhaleFraction)
      : Math.PI + Math.PI * (
        (cycleT - inhaleFraction) / this.m_breathExhaleFraction
      );
    return Math.sin(phase) * this.m_breathPitchAmplitudeDeg;
  }

  /** Xorshift32 — visual variation only, never used for security. */
  private nextRandom(): number {
    let x = this.m_randomState;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.m_randomState = x >>> 0;
    return this.m_randomState / 0x1_0000_0000;
  }

  /** Triangular distribution, biased toward small corrections. */
  private centeredRandom(): number {
    return this.nextRandom() + this.nextRandom() - 1;
  }

  private randomRange(min: number, max: number): number {
    return lerp(min, max, this.nextRandom());
  }

  /** Independent xorshift32 stream for sub-fixation motion. */
  private nextMicroRandom(): number {
    let x = this.m_microRandomState;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.m_microRandomState = x >>> 0;
    return this.m_microRandomState / 0x1_0000_0000;
  }

  private microCenteredRandom(): number {
    return this.nextMicroRandom() + this.nextMicroRandom() - 1;
  }

  private microRandomRange(min: number, max: number): number {
    return lerp(min, max, this.nextMicroRandom());
  }

  /** Independent again so breath/correction boundary order cannot reshuffle. */
  private nextBreathRandom(): number {
    let x = this.m_breathRandomState;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.m_breathRandomState = x >>> 0;
    return this.m_breathRandomState / 0x1_0000_0000;
  }

  private breathRandomRange(min: number, max: number): number {
    return lerp(min, max, this.nextBreathRandom());
  }

  private publishOffsets(): IdleHeadOffsets {
    if (Math.abs(this.m_yawDeg) < K_OFFSET_SNAP_EPSILON_DEG) this.m_yawDeg = 0;
    if (Math.abs(this.m_pitchDeg) < K_OFFSET_SNAP_EPSILON_DEG) this.m_pitchDeg = 0;
    if (Math.abs(this.m_rollDeg) < K_OFFSET_SNAP_EPSILON_DEG) this.m_rollDeg = 0;
    const macroMagnitudeDeg = Math.hypot(this.m_yawDeg, this.m_pitchDeg);
    const macroFade = 1 - smoothstep(
      K_MICRO_MACRO_FADE_START_DEG,
      K_MICRO_MACRO_FADE_END_DEG,
      macroMagnitudeDeg,
    );
    const microGain = this.m_microVisibility * macroFade;
    this.m_offsets.yawDeg = snapSmall(
      this.m_yawDeg + this.m_microYawDeg * microGain,
    );
    this.m_offsets.pitchDeg = snapSmall(
      this.m_pitchDeg +
        (this.m_microPitchDeg + this.breathPitchDeg()) * microGain,
    );
    this.m_offsets.rollDeg = snapSmall(
      this.m_rollDeg + this.m_microRollDeg * microGain,
    );
    return this.m_offsets;
  }
}

/**
 * Schedules a real user check-in while Spark attends to a non-user subject.
 * `true` means the caller should temporarily resolve the head target to the
 * camera; `false` means it should resolve the latest subject again.
 */
export class NaturalUserCheckIn {
  private m_randomState: number;
  private m_phase: UserCheckInPhase = "waiting";
  private m_phaseRemainingS = 0;
  private m_enabled = false;

  constructor(seed: number) {
    const normalizedSeed = Number.isFinite(seed) ? Math.floor(seed) >>> 0 : 0;
    this.m_randomState =
      (normalizedSeed ^ K_CHECK_IN_RANDOM_SALT) >>> 0 || K_FALLBACK_RANDOM_STATE;
  }

  reset(): void {
    this.m_phase = "waiting";
    this.m_phaseRemainingS = 0;
    this.m_enabled = false;
  }

  /**
   * Start one check-in without consuming the ordinary wait. The caller owns
   * one-shot semantics (for example, one request per pointer acquisition);
   * after the return reaches that subject this controller resumes its normal
   * repeat cadence.
   */
  requestImmediate(): void {
    this.m_enabled = true;
    this.m_phase = "turning";
    this.m_phaseRemainingS = 0;
  }

  update(
    deltaSeconds: number,
    enabled: boolean,
    atUser: boolean,
    atSubject: boolean,
    subjectStable: boolean,
  ): boolean {
    const dt = clamp(
      Number.isFinite(deltaSeconds) ? deltaSeconds : 0,
      0,
      K_MAX_FRAME_STEP_S,
    );

    if (!enabled) {
      if (this.m_enabled) this.reset();
      return false;
    }

    if (!this.m_enabled) {
      this.m_enabled = true;
      this.m_phase = "waiting";
      this.m_phaseRemainingS = this.randomRange(
        K_CHECK_IN_INITIAL_WAIT_MIN_S,
        K_CHECK_IN_INITIAL_WAIT_MAX_S,
      );
    }

    if (this.m_phase === "waiting") {
      if (atSubject && subjectStable) this.m_phaseRemainingS -= dt;
      if (this.m_phaseRemainingS <= K_PHASE_EPSILON_S) {
        this.m_phase = "turning";
        return true;
      }
      return false;
    }

    if (this.m_phase === "turning") {
      if (atUser) {
        this.m_phase = "holding";
        this.m_phaseRemainingS = this.randomRange(
          K_CHECK_IN_HOLD_MIN_S,
          K_CHECK_IN_HOLD_MAX_S,
        );
      }
      return true;
    }

    if (this.m_phase === "holding") {
      this.m_phaseRemainingS -= dt;
      if (this.m_phaseRemainingS > K_PHASE_EPSILON_S) return true;
      this.m_phase = "returning";
      return false;
    }

    if (atSubject) {
      this.m_phase = "waiting";
      this.m_phaseRemainingS = this.randomRange(
        K_CHECK_IN_REPEAT_WAIT_MIN_S,
        K_CHECK_IN_REPEAT_WAIT_MAX_S,
      );
    }
    return false;
  }

  private nextRandom(): number {
    let x = this.m_randomState;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.m_randomState = x >>> 0;
    return this.m_randomState / 0x1_0000_0000;
  }

  private randomRange(min: number, max: number): number {
    return lerp(min, max, this.nextRandom());
  }
}

/**
 * Seeded short-form reaction for a pointer entering Spark's personal space.
 * `true` selects the user; `false` selects the live captured pointer target.
 * A disabled frame (speech) pauses rather than consumes the reaction.
 */
export class NaturalHeadIntrusionReaction {
  private m_randomState: number;
  private m_phase: HeadIntrusionPhase = "inactive";
  private m_phaseRemainingS = 0;

  constructor(seed: number) {
    const normalizedSeed = Number.isFinite(seed) ? Math.floor(seed) >>> 0 : 0;
    this.m_randomState =
      (normalizedSeed ^ K_HEAD_INTRUSION_RANDOM_SALT) >>> 0 ||
      K_FALLBACK_RANDOM_STATE;
  }

  get isActive(): boolean {
    return this.m_phase !== "inactive";
  }

  trigger(): void {
    if (this.isActive) return;
    this.m_phase = "pointer";
    this.m_phaseRemainingS = this.randomRange(
      K_HEAD_INTRUSION_POINTER_HOLD_MIN_S,
      K_HEAD_INTRUSION_POINTER_HOLD_MAX_S,
    );
  }

  cancel(): void {
    this.m_phase = "inactive";
    this.m_phaseRemainingS = 0;
  }

  update(
    deltaSeconds: number,
    enabled: boolean,
  ): boolean {
    if (!this.isActive) return false;
    if (!enabled) return this.m_phase === "user";

    const dt = clamp(
      Number.isFinite(deltaSeconds) ? deltaSeconds : 0,
      0,
      K_MAX_FRAME_STEP_S,
    );
    this.m_phaseRemainingS -= dt;
    if (this.m_phaseRemainingS <= K_PHASE_EPSILON_S) {
      if (this.m_phase === "user") {
        this.m_phase = "pointer";
        this.m_phaseRemainingS = this.randomRange(
          K_HEAD_INTRUSION_REPEAT_WAIT_MIN_S,
          K_HEAD_INTRUSION_REPEAT_WAIT_MAX_S,
        );
      } else {
        this.m_phase = "user";
        this.m_phaseRemainingS = K_HEAD_INTRUSION_USER_HOLD_S;
      }
    }
    return this.m_phase === "user";
  }

  private nextRandom(): number {
    let x = this.m_randomState;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.m_randomState = x >>> 0;
    return this.m_randomState / 0x1_0000_0000;
  }

  private randomRange(min: number, max: number): number {
    return lerp(min, max, this.nextRandom());
  }
}

function approachZero(value: number, t: number): number {
  const next = lerp(value, 0, t);
  return Math.abs(next) < K_OFFSET_SNAP_EPSILON_DEG ? 0 : next;
}

function snapSmall(value: number): number {
  return Math.abs(value) < K_OFFSET_SNAP_EPSILON_DEG ? 0 : value;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clamp((value - edge0) / Math.max(K_PHASE_EPSILON_S, edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Quintic minimum-jerk curve: zero velocity and acceleration at both ends. */
function minimumJerk(t: number): number {
  const x = clamp(t, 0, 1);
  return x * x * x * (10 + x * (-15 + x * 6));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
